import { OVERLAP_STRATEGIES } from '@connaro/shared';
import { useState } from 'react';
import { ApiError, api } from '../api/client';
import { useAsync } from '../hooks/useAsync';
import { useT } from '../i18n/I18nProvider';
import { AsyncError, Banner, EmptyState, Loading } from './states';

interface Overlap {
  key: string;
  a: { owner: string; cidr: string };
  b: { owner: string; cidr: string };
  resolved: boolean;
  strategy: string | null;
}

/**
 * Overlapping-prefix conflicts for one fabric, and the operator's resolution.
 *
 * Two sites advertising the same address space make routing ambiguous. The
 * platform never resolves that silently, so each conflict blocks deployment until
 * someone declares how it is handled — exclude, translate, or isolate. This panel
 * surfaces the conflicts the deployment would otherwise carry and records the
 * decision; an unresolved row is why a fabric will not deploy.
 */
export function OverlapsPanel({ fabricId }: { fabricId: string }): JSX.Element {
  const t = useT();
  const { data, error, loading, reload } = useAsync(
    () => api.get<Overlap[]>(`/sdwan/fabrics/${fabricId}/overlaps`),
    [fabricId],
  );
  const [choice, setChoice] = useState<Record<string, string>>({});
  const [submitError, setSubmitError] = useState<string | null>(null);

  const strategyFor = (key: string): string => choice[key] ?? OVERLAP_STRATEGIES[0];

  const resolve = async (key: string): Promise<void> => {
    setSubmitError(null);
    try {
      await api.post(`/sdwan/fabrics/${fabricId}/overlaps/resolve`, {
        key,
        strategy: strategyFor(key),
      });
      reload();
    } catch (err) {
      setSubmitError(err instanceof ApiError ? err.message : t('errors.unexpected'));
    }
  };

  const unresolve = async (key: string): Promise<void> => {
    setSubmitError(null);
    try {
      await api.del(`/sdwan/fabrics/${fabricId}/overlaps/resolve`, { key });
      reload();
    } catch (err) {
      setSubmitError(err instanceof ApiError ? err.message : t('errors.unexpected'));
    }
  };

  // Guard against a non-array payload (a loading/error state or an unexpected response)
  // so the panel degrades to "no overlaps" rather than throwing on `.filter`/`.map`.
  const overlaps = Array.isArray(data) ? data : [];
  const unresolvedCount = overlaps.filter((o) => !o.resolved).length;

  return (
    <div className="card">
      <h2>{t('overlaps.title')}</h2>
      <p>{t('overlaps.intro')}</p>
      {submitError ? <Banner variant="error">{submitError}</Banner> : null}

      {loading ? (
        <Loading />
      ) : error ? (
        <AsyncError error={error} onRetry={reload} />
      ) : overlaps.length === 0 ? (
        <EmptyState hint={t('overlaps.none')} />
      ) : (
        <>
          {unresolvedCount > 0 ? (
            <Banner variant="warning">
              {t('overlaps.blocking').replace('{count}', String(unresolvedCount))}
            </Banner>
          ) : (
            <Banner variant="success">{t('overlaps.allResolved')}</Banner>
          )}
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>{t('overlaps.conflict')}</th>
                  <th>{t('overlaps.status')}</th>
                  <th aria-label="actions" />
                </tr>
              </thead>
              <tbody>
                {overlaps.map((o) => (
                  <tr key={o.key}>
                    <td>
                      <code>{o.a.cidr}</code> ↔ <code>{o.b.cidr}</code>
                    </td>
                    <td>
                      {o.resolved ? (
                        <span className="chip chip--success">{o.strategy}</span>
                      ) : (
                        <span className="chip chip--critical">{t('overlaps.unresolved')}</span>
                      )}
                    </td>
                    <td className="row-actions">
                      {o.resolved ? (
                        <button
                          type="button"
                          className="btn btn--ghost btn--danger"
                          onClick={() => void unresolve(o.key)}
                        >
                          {t('overlaps.withdraw')}
                        </button>
                      ) : (
                        <>
                          <label className="sr-only" htmlFor={`strat-${o.key}`}>
                            {t('overlaps.strategy')}
                          </label>
                          <select
                            id={`strat-${o.key}`}
                            value={strategyFor(o.key)}
                            onChange={(e) => setChoice((c) => ({ ...c, [o.key]: e.target.value }))}
                          >
                            {OVERLAP_STRATEGIES.map((strat) => (
                              <option key={strat} value={strat}>
                                {t(`overlaps.strategies.${strat}`)}
                              </option>
                            ))}
                          </select>
                          <button type="button" className="btn" onClick={() => void resolve(o.key)}>
                            {t('overlaps.resolve')}
                          </button>
                        </>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}
